import { useState, useMemo } from 'react';
import { useMetaData, useApi } from '../hooks/useApi';
import { useCategory } from '../context/CategoryContext';
import { useMonth } from '../context/MonthContext';
import { Gender } from '../utils/api';
import { LineChart, ChartCard, ChartLoader, ChartError } from '../components/Charts';
import { GENDER_COLORS, fmtMonth } from '../utils/theme';

const GENDER_LABEL = { M: 'Male', F: 'Female', U: 'Unknown' };

function buildGenderSeries(rows, months6, valueKey) {
  const genders = [...new Set(rows.map(r => r.gender))].sort();
  return genders.map(g => {
    const byMonth = Object.fromEntries(rows.filter(r => r.gender === g).map(r => [r.month_id, r[valueKey]]));
    return { name: GENDER_LABEL[g] || g, data: months6.map(m => byMonth[m] ?? null), color: GENDER_COLORS[g] };
  });
}

function last6(allMonths, selectedMonth) {
  const idx = allMonths.indexOf(selectedMonth);
  if (idx < 0) return allMonths.slice(-6);
  return allMonths.slice(Math.max(0, idx - 5), idx + 1);
}

export default function TrendsGenderView() {
  const { category }   = useCategory();
  const { months, sessionTypes, latestMonth, loading: metaLoading } = useMetaData();

  const { month }                     = useMonth();
  const [sessionType, setSessionType] = useState('open_app');

  const effectiveCat  = category || 'ALL';
  const selectedMonth = month || latestMonth;
  const months6       = useMemo(() => last6(months, selectedMonth), [months, selectedMonth]);

  const { data, loading, error } = useApi(
    () => Gender.trend({ category: effectiveCat, session_type: sessionType }),
    { effectiveCat, sessionType },
    !!months.length,
  );

  const userSeries    = useMemo(() => data ? buildGenderSeries(data, months6, 'active_users')            : [], [data, months6]);
  const sessionSeries = useMemo(() => data ? buildGenderSeries(data, months6, 'total_15min_sessions') : [], [data, months6]);

  if (metaLoading) return <div className="loading-state"><div className="spinner" /> Loading…</div>;

  return (
    <div>
      <div className="filters-bar">
        <span className="filter-label">Session</span>
        <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
          {sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="trends-section-label">By Gender — 6-Month Trend</div>

      <ChartCard title="Monthly Active Users — by Gender" sub={`${effectiveCat} · ${sessionType}`} style={{ marginBottom: 16 }}>
        {loading  && <ChartLoader height={300} />}
        {error    && <ChartError message={error} height={300} />}
        {!loading && userSeries.length > 0 && <LineChart categories={months6.map(fmtMonth)} series={userSeries} height={300} />}
        {!loading && !error && userSeries.length === 0 && <div className="empty-state">No data</div>}
      </ChartCard>

      <ChartCard title="Monthly Sessions — by Gender" sub={`${effectiveCat} · ${sessionType}`}>
        {loading  && <ChartLoader height={300} />}
        {error    && <ChartError message={error} height={300} />}
        {!loading && sessionSeries.length > 0 && <LineChart categories={months6.map(fmtMonth)} series={sessionSeries} height={300} />}
        {!loading && !error && sessionSeries.length === 0 && <div className="empty-state">No data</div>}
      </ChartCard>
    </div>
  );
}
